/**
 * Hang the reader's call site on a PouchDB error as its `cause`, so Sentry and the log name the
 * code that asked for the document (#6278). The error is otherwise left exactly as it was.
 *
 * It acts only on a fresh PouchDB `Error` (numeric `status`, string `name`) with no `cause` yet.
 * PouchDB hands some error objects to more than one reader, and stamping those would name the
 * wrong one; which they are, and the rest of the story: design-docs/pass-through-db.md,
 * "A failed read names its caller too (#6278)".
 */
export function attachReadCallSite(err: unknown, callSite: Error): void {
  // a plain object (a remote response, or the remote adapter's one shared setup rejection) is
  // skipped: it may be shared, and Sentry ignores a `cause` on anything that is not an Error
  if (!(err instanceof Error)) return;
  const fields = err as Error & { status?: unknown };
  if (typeof fields.status !== 'number') return;
  // PouchDB returns its one module-level UNKNOWN_ERROR object itself, not a copy
  if (fields.name === 'unknown_error') return;
  if (fields.cause !== undefined) return;
  // Reflect, not Object: a frozen or sealed object answers false instead of throwing, and then
  // simply keeps what it had
  Reflect.defineProperty(err, 'cause', { value: callSite, enumerable: false, writable: true, configurable: true });
}
